import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import useTokenRevocation from '../../../../hooks/useTokenRevocation';
import usePlaybackTokenDetailsByJti from '../../../../hooks/usePlaybackTokenDetailsByJti';
import { BadgeListItem } from '../../../../styledComponents/styledComponents';
import {
   BlockStatusDot,
   BlockStatusText,
   BlockActionButton,
   BlockStatusRow,
   BlockRowGrid,
   BlockRowControls,
   BlockRowStatusLine,
   BlockErrorText,
} from '../StyledComponents';
import moment from 'moment';

const REVOCATION_GRACE_PERIOD_HOURS = 24;

// revokedMap: { [playbackToken]: true } - undefined while loading
function TokenRevokeRow({ jti, from, to, revokedMap, onActionSuccess }) {
   const [copied, setCopied] = useState(false);

   const { tokenDetails, isLoading: isTokenLoading } = usePlaybackTokenDetailsByJti({ jti, from, to });

   const { tokenPayload, jwt } = tokenDetails || {};
   const expirationMoment = moment.utc(tokenPayload?.exp);

   const now = moment.utc();
   const isExpired = expirationMoment.isValid() && expirationMoment.isBefore(now);
   const isExpiredOverGracePeriod =
      expirationMoment.isValid() &&
      expirationMoment.isBefore(now.clone().subtract(REVOCATION_GRACE_PERIOD_HOURS, 'hours'));

   const isRevocable = tokenPayload?.revocable === true;
   const isRevoked = revokedMap !== undefined && jti ? revokedMap[jti] === true : undefined;

   const { revoke, isLoading: isRevoking, isSuccess, error, reset } = useTokenRevocation({ token: jwt });

   useEffect(() => {
      if (isSuccess) {
         onActionSuccess();
         reset();
      }
   }, [isSuccess, onActionSuccess, reset]);

   const handleCopy = async () => {
      await navigator.clipboard.writeText(jti).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
   };

   return (
      <BlockRowGrid>
         <BadgeListItem $fontSizePx={13} $cursor='pointer' onClick={handleCopy} className='vertical-padding'>
            {copied ? `✓ ${jti}` : jti}
         </BadgeListItem>

         <BlockRowControls>
            {isTokenLoading || isRevoked === undefined ? (
               ''
            ) : (
               <BlockRowStatusLine>
                  {isRevocable ? (
                     <>
                        <BlockStatusRow>
                           <BlockStatusDot $blocked={isRevoked} />
                           <BlockStatusText $blocked={isRevoked}>
                              {isRevoked ? 'Revoked' : 'Not revoked'}
                           </BlockStatusText>
                        </BlockStatusRow>
                        {isExpired && <BlockStatusText>{' | Expired'}</BlockStatusText>}
                        {!isRevoked && !isExpiredOverGracePeriod && (
                           <BlockActionButton $variant='block' disabled={isRevoking || !jwt} onClick={revoke}>
                              {isRevoking ? 'Revoking...' : 'Revoke'}
                           </BlockActionButton>
                        )}
                     </>
                  ) : (
                     <BlockStatusText>Token is not revocable</BlockStatusText>
                  )}
               </BlockRowStatusLine>
            )}

            {error && <BlockErrorText>{error.message}</BlockErrorText>}
         </BlockRowControls>
      </BlockRowGrid>
   );
}

TokenRevokeRow.propTypes = {
   jti: PropTypes.string.isRequired,
   from: PropTypes.string.isRequired,
   to: PropTypes.string.isRequired,
   revokedMap: PropTypes.object,
   onActionSuccess: PropTypes.func.isRequired,
};

export default TokenRevokeRow;
